import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Box, Typography, Button, Chip, Collapse, TextField, IconButton,
  Avatar, Divider, CircularProgress
} from '@mui/material';
import {
  Flag, ExpandMore, ExpandLess, Send, OpenInNew, RequestQuote, AccountBalanceWallet,
  Construction, Event, CheckCircle, RateReview, SwapHoriz, Forum, Circle
} from '@mui/icons-material';
import messagingService from '../../services/messagingService';
import TrustLine from '../contractors/TrustLine';
import { QuoteNegotiationActions } from '../QuoteAffordability';
import api from '../../services/api';
import { brand, text, bg, border, status as statusTokens } from '../../theme/colors';

/**
 * "Where things stand" — the project hub card. One prioritised next action,
 * a dated timeline, quotations awaiting a decision and the thread with the
 * other party, so every email/WhatsApp link can land here and the reader
 * knows what happened and what to do without hunting.
 *
 * Data: GET /projects/:id/hub (ProjectHubService). `onAction` receives
 * { action:'fund' } or { scrollTo:'milestones'|'find-contractors'|... } so
 * the page can open dialogs / scroll to its own sections.
 */
const TONE = {
  urgent: { bg: statusTokens.errorBg, fg: statusTokens.errorDark, accent: statusTokens.error },
  action: { bg: statusTokens.warningBg, fg: statusTokens.warningDark, accent: statusTokens.warning },
  info: { bg: statusTokens.infoBg, fg: statusTokens.infoDark, accent: statusTokens.info },
  success: { bg: statusTokens.successBg, fg: statusTokens.successDark, accent: statusTokens.success },
  neutral: { bg: bg.subtle, fg: text.secondary, accent: text.tertiary },
};

const KIND_ICON = {
  project: <Flag />, quote: <RequestQuote />, accepted: <CheckCircle />, declined: <Circle />,
  assessment: <Event />, visit: <Event />, money: <AccountBalanceWallet />, stage: <Construction />,
  change: <SwapHoriz />, done: <CheckCircle />, review: <RateReview />,
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtTime = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const fmtMoney = (n, c = 'USD') => (n === null || n === undefined ? '' : `${c} ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);

const QUOTE_STATUS = {
  submitted: { label: 'Awaiting your decision', color: statusTokens.warningDark, bgc: statusTokens.warningBg },
  under_review: { label: 'Under review', color: statusTokens.infoDark, bgc: statusTokens.infoBg },
  changes_requested: { label: 'Changes requested', color: statusTokens.infoDark, bgc: statusTokens.infoBg },
  accepted: { label: 'Accepted', color: statusTokens.successDark, bgc: statusTokens.successBg },
  rejected: { label: 'Declined', color: text.tertiary, bgc: bg.subtle },
  withdrawn: { label: 'Withdrawn', color: text.tertiary, bgc: bg.subtle },
};

function MiniThread({ thread, currentUserId, projectId, onOpened }) {
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState(thread?.conversationId || null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async (convId) => {
    if (!convId) return;
    setLoading(true);
    try {
      const res = await messagingService.getMessages(convId, 1);
      const list = res?.data?.messages || res?.messages || res?.data || [];
      // Order defensively (the API has paginated both ways over time) and
      // show the newest few, oldest→newest like a chat.
      const sorted = [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMessages(sorted.slice(-4));
      onOpened?.();
    } catch (_) { /* keep the card usable without the thread */ }
    finally { setLoading(false); }
  }, [onOpened]);

  useEffect(() => { setConversationId(thread?.conversationId || null); }, [thread?.conversationId]);
  useEffect(() => { if (conversationId) load(conversationId); }, [conversationId, load]);

  const ensureConversation = async () => {
    if (conversationId) return conversationId;
    const conv = await messagingService.getOrCreateConversation(thread.otherUserId, projectId);
    const id = conv?.id || conv?.conversation?.id || conv?.data?.id;
    setConversationId(id);
    return id;
  };

  const send = async () => {
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    try {
      const id = await ensureConversation();
      await messagingService.sendMessage(id, content);
      setDraft('');
      await load(id);
    } catch (_) { /* surfaced by the full messages page if it keeps failing */ }
    finally { setSending(false); }
  };

  if (!thread) return null;
  return (
    <Box id="project-hub-messages" sx={{ mt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Forum sx={{ fontSize: 18, color: brand.primary }} /> Messages with {thread.otherName}
          {thread.unread > 0 && <Chip size="small" label={`${thread.unread} new`} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: statusTokens.errorBg, color: statusTokens.errorDark }} />}
        </Typography>
        {conversationId && (
          <Button size="small" endIcon={<OpenInNew sx={{ fontSize: 14 }} />} onClick={() => navigate(`/messages?conversation=${conversationId}`)} sx={{ textTransform: 'none' }}>
            Open full conversation
          </Button>
        )}
      </Box>
      <Box sx={{ border: `1px solid ${border.default}`, borderRadius: 2, p: 1.5, bgcolor: bg.subtle }}>
        {loading && messages.length === 0 && <CircularProgress size={18} />}
        {!loading && messages.length === 0 && (
          <Typography variant="body2" sx={{ color: text.tertiary, mb: 1 }}>
            No messages yet. Anything you send here reaches {thread.otherName} on Eqaya and on WhatsApp.
          </Typography>
        )}
        {messages.map((m) => {
          const mine = String(m.sender_id) === String(currentUserId);
          return (
            <Box key={m.id} sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 0.75 }}>
              <Box sx={{ maxWidth: '80%', px: 1.5, py: 0.75, borderRadius: 2, bgcolor: mine ? brand.primary : '#fff', color: mine ? '#fff' : text.primary, border: mine ? 'none' : `1px solid ${border.default}` }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content || (m.attachment_url ? '📎 Attachment' : '')}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: mine ? 'rgba(255,255,255,0.85)' : text.tertiary }}>{fmtTime(m.created_at)}</Typography>
              </Box>
            </Box>
          );
        })}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small" fullWidth placeholder={`Message ${thread.otherName}…`} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            sx={{ bgcolor: '#fff' }}
          />
          <IconButton color="primary" onClick={send} disabled={sending || !draft.trim()} aria-label="Send message">
            {sending ? <CircularProgress size={18} /> : <Send />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

const ProjectHubCard = ({ hub, loading, currentUserId, projectId, onAction, highlightBidId }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const [fundingSaving, setFundingSaving] = useState(false);

  if (loading && !hub) {
    return (
      <Card sx={{ mb: 3 }}><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={20} /> <Typography variant="body2" color="text.secondary">Working out where things stand…</Typography>
      </CardContent></Card>
    );
  }
  if (!hub) return null;

  const tone = TONE[hub.next?.tone] || TONE.neutral;
  const events = hub.timeline || [];
  const recent = showAll ? events : events.slice(-4);
  const decidable = (hub.quotations || []).filter((q) => ['submitted', 'under_review', 'changes_requested'].includes(q.status));
  const shownQuotes = decidable.length > 0 ? decidable : (hub.quotations || []).filter((q) => q.status === 'accepted');

  const saveFunding = async (preference) => {
    setFundingSaving(true);
    try {
      await api.post(`/projects/${projectId}/funding-preference`, { preference });
      onAction?.({ reload: true });
    } catch (_) { /* the question simply stays on the card */ }
    finally { setFundingSaving(false); }
  };

  const act = () => {
    const n = hub.next;
    if (!n) return;
    if (n.action) return onAction?.({ action: n.action, milestoneId: n.milestoneId });
    if (n.scrollTo) return onAction?.({ scrollTo: n.scrollTo, milestoneId: n.milestoneId });
    if (n.href) navigate(n.href);
  };

  return (
    <Card id="project-hub" sx={{ mb: 3, borderTop: `4px solid ${tone.accent}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography variant="overline" sx={{ color: text.tertiary, fontWeight: 700, letterSpacing: '0.08em' }}>Where things stand</Typography>
          {hub.wallet && (
            <Chip size="small" icon={<AccountBalanceWallet sx={{ fontSize: 14 }} />}
              label={hub.funded ? `Wallet funded · ${fmtMoney(hub.wallet.totalAmount, hub.wallet.currency)}` : 'Wallet not yet funded'}
              sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: hub.funded ? statusTokens.successBg : statusTokens.warningBg, color: hub.funded ? statusTokens.successDark : statusTokens.warningDark }} />
          )}
        </Box>

        {/* Next action */}
        {hub.next && (
          <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, p: 2, borderRadius: 2, bgcolor: tone.bg, border: `1px solid ${tone.accent}33` }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: tone.fg, lineHeight: 1.25 }}>{hub.next.title}</Typography>
              {hub.next.body && <Typography variant="body2" sx={{ color: text.secondary, mt: 0.5 }}>{hub.next.body}</Typography>}
              {hub.next.secondary && (
                <Button size="small" variant="text" onClick={() => onAction?.({ action: hub.next.secondary.action })}
                  sx={{ mt: 0.75, ml: -1, textTransform: 'none', color: text.secondary, fontSize: 12.5 }}>
                  {hub.next.secondary.label}
                </Button>
              )}
            </Box>
            {hub.next.cta && (
              <Button variant="contained" onClick={act} sx={{ bgcolor: tone.accent, '&:hover': { bgcolor: tone.fg }, whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', sm: 'center' } }}>
                {hub.next.cta}
              </Button>
            )}
          </Box>
        )}

        {/* "How would you like to pay?" — one tap; contractors then quote it stage by stage. */}
        {hub.askFunding && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: bg.subtle, border: `1px solid ${border.default}` }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>
              Would you like quotations in stages?
            </Typography>
            <Typography variant="caption" sx={{ color: text.secondary, display: 'block', mb: 1 }}>
              With stages you fund one part of the job at a time into your project wallet, and pay out only when you approve it. Contractors quoting you will be told your answer.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[['stages', 'Yes, in stages'], ['once', 'All at once'], ['unsure', 'Not sure yet']].map(([v, label]) => (
                <Button key={v} size="small" variant={v === 'stages' ? 'contained' : 'outlined'} disabled={fundingSaving}
                  onClick={() => saveFunding(v)} sx={{ textTransform: 'none' }}>
                  {label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
        {hub.fundingPreference === 'stages' && hub.viewer === 'owner' && ['waiting_quotes', 'assessment_reply', 'visit'].includes(hub.next?.key) && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: text.secondary }}>
            Contractors have been asked to quote this in stages — you fund one stage at a time.
          </Typography>
        )}

        {/* Independent site check — sold at the moment of choosing / depositing. */}
        {hub.propertyCheck && ['review_quotes', 'deposit', 'waiting_quotes', 'assessment_reply', 'in_progress', 'review_stage'].includes(hub.next?.key) && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: text.secondary }}>
            Want eyes on the ground? An independent Eqaya verifier can visit the site and send you dated, GPS-tagged photos and a written report, from USD {hub.propertyCheck.fromUsd}.{' '}
            <Box component="a" href={hub.propertyCheck.href} sx={{ color: brand.primary, fontWeight: 600 }}>Book a property check</Box>.
          </Typography>
        )}

        {/* Quotations awaiting a decision */}
        {shownQuotes.length > 0 && (
          <Box id="project-hub-quotations" sx={{ mt: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {decidable.length > 0 ? `Quotations to decide on (${decidable.length})` : 'Accepted quotation'}
            </Typography>
            {shownQuotes.map((q) => {
              const st = QUOTE_STATUS[q.status] || { label: q.status, color: text.secondary, bgc: bg.subtle };
              const hl = highlightBidId && String(highlightBidId) === String(q.id);
              return (
                <Box key={q.id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, borderRadius: 2, border: `1px solid ${hl ? brand.primary : border.default}`, bgcolor: hl ? `${brand.primary}08` : '#fff' }}>
                  <Box sx={{ flex: '1 1 260px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{q.contractorName}</Typography>
                      <Chip size="small" label={st.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: st.color, bgcolor: st.bgc }} />
                    </Box>
                    <TrustLine facts={q.trust} compact />
                    <Typography variant="caption" sx={{ color: text.tertiary }}>{q.bidNumber} · {fmtDate(q.submittedAt)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{fmtMoney(q.totalCost, q.currency)}</Typography>
                    {q.firstStage && q.status !== 'accepted' && (
                      <Typography variant="caption" sx={{ color: statusTokens.successDark, fontWeight: 600 }}>
                        Start with {fmtMoney(q.firstStage.amount, q.currency)}
                      </Typography>
                    )}
                  </Box>
                  <Button variant={q.status === 'accepted' ? 'outlined' : 'contained'} size="small" onClick={() => navigate(`/bids/${q.id}`)}>
                    {q.status === 'accepted' ? 'View' : 'Review'}
                  </Button>
                  {hub.viewer === 'owner' && q.status !== 'accepted' && (
                    <Box sx={{ flexBasis: '100%' }}>
                      <Typography variant="caption" sx={{ color: text.secondary, display: 'block', mb: 0.5 }}>
                        {q.firstStage
                          ? `${q.firstStage.stageCount} stages — fund one at a time, starting with "${q.firstStage.name}". Not the whole ${fmtMoney(q.totalCost, q.currency)} at once.`
                          : `No stage breakdown yet. You don't have to fund ${fmtMoney(q.totalCost, q.currency)} at once — ask for stages, or say what you have available.`}
                      </Typography>
                      <QuoteNegotiationActions kind="bid" id={q.id} total={q.totalCost} currency={q.currency} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Timeline */}
        {events.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>What has happened</Typography>
              {events.length > 4 && (
                <Button size="small" onClick={() => setShowAll((s) => !s)} endIcon={showAll ? <ExpandLess /> : <ExpandMore />} sx={{ textTransform: 'none' }}>
                  {showAll ? 'Show recent' : `Show all ${events.length}`}
                </Button>
              )}
            </Box>
            <Collapse in appear={false}>
              <Box sx={{ mt: 0.5 }}>
                {recent.map((e, i) => (
                  <Box key={`${e.at}-${i}`} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 0.6, borderBottom: i < recent.length - 1 ? `1px dashed ${border.default}` : 'none' }}>
                    <Avatar sx={{ width: 26, height: 26, bgcolor: e.future ? statusTokens.infoBg : bg.subtle, color: e.future ? statusTokens.infoDark : brand.primary, '& svg': { fontSize: 15 } }}>
                      {KIND_ICON[e.kind] || <Circle />}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: text.primary }}>{e.text}</Typography>
                      <Typography variant="caption" sx={{ color: text.tertiary }}>{e.future ? 'Upcoming · ' : ''}{fmtTime(e.at)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {hub.thread && (
          <>
            <Divider sx={{ mt: 2.5 }} />
            <MiniThread thread={hub.thread} currentUserId={currentUserId} projectId={projectId} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectHubCard;
